(() => {
  const VERSION = '2026.08.26.13';
  const previous = window.BHJudgeReviews;
  if (previous?.version === VERSION) {
    previous.refresh?.();
    return;
  }
  previous?.destroy?.();

  document.documentElement.classList.add('bh-judge-reviews-ready');

  const abortController = new AbortController();
  const { signal } = abortController;
  const instances = new Map();
  let observer = null;

  const one = (root, selector) => root.querySelector(selector);
  const all = (root, selector) => [...root.querySelectorAll(selector)];
  const clean = value => String(value || '').replace(/\s+/g, ' ').trim();
  const text = (root, selector) => clean(one(root, selector)?.textContent);
  const MOBILE_QUERY = window.matchMedia('(max-width: 649px)');
  const TABLET_QUERY = window.matchMedia('(max-width: 989px)');
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

  function element(tag, className, content) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content !== undefined) node.textContent = content;
    return node;
  }

  function parseScore(value, fallback = 5) {
    const match = String(value || '').match(/\d+(?:[.,]\d+)?/);
    return match ? Math.min(5, Math.max(0, Number(match[0].replace(',', '.')))) : fallback;
  }

  function createStars(score, compact = false) {
    const stars = element('span', compact ? 'bh-reviews__stars bh-review-card__stars' : 'bh-reviews__stars');
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${score.toFixed(1)} de 5 estrellas`);
    for (let index = 1; index <= 5; index += 1) {
      const star = element('span', 'bh-reviews__star');
      star.setAttribute('aria-hidden', 'true');
      star.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path class="bh-reviews__star-base" d="M12 2.8l2.73 5.53 6.1.89-4.42 4.31 1.04 6.08L12 16.74 6.55 19.6l1.04-6.08-4.42-4.31 6.1-.89L12 2.8Z"/><path class="bh-reviews__star-fill" d="M12 2.8l2.73 5.53 6.1.89-4.42 4.31 1.04 6.08L12 16.74 6.55 19.6l1.04-6.08-4.42-4.31 6.1-.89L12 2.8Z"/></svg>';
      if (score >= index - 0.2) star.classList.add('is-on');
      else if (score >= index - 0.75) star.classList.add('is-half');
      stars.append(star);
    }
    return stars;
  }

  function collectReviews(source) {
    const reviews = [];
    const seen = new Set();
    const items = all(source, '.jdgm-carousel-item');
    const productUrls = new Map();

    items.forEach(item => {
      const productTitle = text(item, '.jdgm-carousel-item__product-title').toLocaleLowerCase('es');
      const product = one(item, 'a.jdgm-carousel-item__product[href], .jdgm-carousel-item__product a[href], a.jdgm-carousel-item__product-title[href]');
      if (productTitle && product?.getAttribute('href')) productUrls.set(productTitle, product.getAttribute('href'));
    });

    items.forEach(item => {
      const id = item.dataset.reviewId || clean(item.textContent).slice(0, 120);
      if (!id || seen.has(id)) return;
      seen.add(id);

      const product = one(item, '.jdgm-carousel-item__product');
      const productLink = one(item, 'a.jdgm-carousel-item__product[href], .jdgm-carousel-item__product a[href], a.jdgm-carousel-item__product-title[href]');
      const image = one(item, '.jdgm-carousel-item__product-image');
      const reviewTitle = text(item, '.jdgm-carousel-item__review-title');
      const reviewBody = text(item, '.jdgm-carousel-item__review-body');
      const productTitle = text(item, '.jdgm-carousel-item__product-title');
      const sourceAuthor = text(item, '.jdgm-carousel-item__reviewer-name');
      const anonymousAuthor = !sourceAuthor || /^(an[oó]nimo|anonymous)$/i.test(sourceAuthor);
      const author = anonymousAuthor ? '' : sourceAuthor;

      const directProductUrl = productLink?.getAttribute('href')
        || (product?.tagName === 'A' ? product.getAttribute('href') : '');
      if (!reviewTitle && !reviewBody) return;

      reviews.push({
        id,
        author,
        score: parseScore(one(item, '.jdgm-carousel-item__review-rating')?.getAttribute('aria-label')),
        title: reviewTitle,
        body: reviewBody,
        productTitle,
        productUrl: directProductUrl
          || productUrls.get(productTitle.toLocaleLowerCase('es'))
          || (productTitle ? `/search?type=product&q=${encodeURIComponent(productTitle)}` : ''),
        imageUrl: image?.getAttribute('data-src-retina') || image?.getAttribute('data-src') || image?.getAttribute('src') || ''
      });
    });
    return reviews.slice(0, 9);
  }

  function createCard(review) {
    const card = element('article', 'bh-review-card');
    card.dataset.reviewId = review.id;

    const meta = element('div', `bh-review-card__meta${review.author ? '' : ' is-anonymous'}`);
    const authorCopy = element('div', 'bh-review-card__author-copy');
    if (review.author) {
      const initial = clean(review.author).charAt(0).toUpperCase();
      meta.append(element('span', 'bh-review-card__avatar', initial));
      authorCopy.append(element('p', 'bh-review-card__author', review.author));
    }
    authorCopy.append(createStars(review.score, true));
    meta.append(authorCopy);
    card.append(meta);

    const content = element('div', 'bh-review-card__content');
    if (review.title) content.append(element('h3', 'bh-review-card__title', review.title));
    if (review.body) content.append(element('p', 'bh-review-card__body', review.body));
    card.append(content);

    if (review.productTitle || review.imageUrl) {
      const product = element(review.productUrl ? 'a' : 'div', 'bh-review-card__product');
      if (review.productUrl) {
        product.href = review.productUrl;
        product.setAttribute('aria-label', `Ver ${review.productTitle || 'producto reseñado'}`);
      }
      if (review.imageUrl) {
        const image = element('img', 'bh-review-card__image');
        image.src = review.imageUrl;
        image.alt = review.productTitle || 'Producto reseñado';
        image.loading = 'lazy';
        image.decoding = 'async';
        product.append(image);
      } else {
        product.append(element('span', 'bh-review-card__image'));
      }
      const productCopy = element('span', 'bh-review-card__product-copy');
      productCopy.append(element('span', 'bh-review-card__product-label', review.productUrl ? 'Ver producto →' : 'Producto reseñado'));
      productCopy.append(element('span', 'bh-review-card__product-title', review.productTitle || 'Producto Bambú Home'));
      product.append(productCopy);
      card.append(product);
    }

    return card;
  }

  function createArrow(direction) {
    const button = element('button', `bh-reviews__arrow bh-reviews__arrow--${direction}`);
    button.type = 'button';
    button.setAttribute('aria-label', direction === 'prev' ? 'Reseñas anteriores' : 'Reseñas siguientes');
    button.innerHTML = direction === 'prev'
      ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7"/></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg>';
    return button;
  }

  function build(source, reviews) {
    const root = element('section', 'bh-reviews');
    root.dataset.bhReviews = '';
    root.setAttribute('aria-label', 'Opiniones de clientes');

    const title = 'Nuestros clientes hablan por nosotros 💚';
    const scoreNode = one(source, '.jdgm-all-reviews-rating');
    const score = parseScore(scoreNode?.dataset.score || scoreNode?.getAttribute('aria-label'), 5);
    const reviewCount = one(source, '.jdgm-carousel-number-of-reviews')?.dataset.numberOfReviews || reviews.length;

    const header = element('header', 'bh-reviews__header');
    header.append(element('p', 'bh-reviews__eyebrow', 'RESEÑAS DE CLIENTES'));
    header.append(element('h2', 'bh-reviews__title', title));
    const summary = element('div', 'bh-reviews__summary');
    summary.append(createStars(score));
    summary.append(element('strong', 'bh-reviews__summary-score', score.toFixed(1)));
    summary.append(element('span', '', `${reviewCount} reseñas`));
    summary.append(element('span', 'bh-reviews__verified', '✓'));
    header.append(summary);
    root.append(header);

    const stage = element('div', 'bh-reviews__stage');
    const viewport = element('div', 'bh-reviews__viewport');
    viewport.tabIndex = 0;
    viewport.setAttribute('aria-label', 'Carrusel de reseñas');
    const track = element('div', 'bh-reviews__track');
    reviews.forEach(review => track.append(createCard(review)));
    viewport.append(track);
    const previousButton = createArrow('prev');
    const nextButton = createArrow('next');
    stage.append(viewport, previousButton, nextButton);
    root.append(stage);

    const pagination = element('div', 'bh-reviews__pagination');
    pagination.setAttribute('role', 'group');
    pagination.setAttribute('aria-label', 'Páginas de reseñas');
    root.append(pagination);

    return { root, viewport, track, previousButton, nextButton, pagination, reviews };
  }

  function createController(source, view) {
    const { root, viewport, track, previousButton, nextButton, pagination, reviews } = view;
    const localController = new AbortController();
    const localSignal = localController.signal;
    let page = 0;
    let pages = 1;
    let timer = 0;
    let scrollFrame = 0;
    let heightFrame = 0;
    let resizeFrame = 0;
    let loopResetTimer = 0;
    let looping = false;
    let visible = true;
    let paused = false;
    let lastColumnCount = 0;

    function columns() {
      if (MOBILE_QUERY.matches) return 1;
      if (TABLET_QUERY.matches) return 2;
      return 3;
    }

    function schedule() {
      window.clearTimeout(timer);
      if (paused || !visible || pages <= 1 || REDUCED_MOTION_QUERY.matches) return;
      timer = window.setTimeout(() => goTo(page + 1, true), 6500);
    }

    function updateControls() {
      previousButton.disabled = page <= 0;
      nextButton.disabled = pages <= 1;
      all(pagination, '.bh-reviews__dot').forEach((dot, index) => {
        const active = index === page;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
    }

    function setFixedHeight() {
      if (heightFrame) window.cancelAnimationFrame(heightFrame);
      heightFrame = window.requestAnimationFrame(() => {
        heightFrame = 0;
        const originalCards = all(track, '.bh-review-card:not([data-bh-review-loop-item])');
        // Las tarjetas nunca reciben altura inline; basta una sola fase de
        // lectura. Antes se escribía en todas y luego se forzaba layout.
        const height = Math.ceil(Math.max(0, ...originalCards.map(card => card.scrollHeight)));
        if (!height) return;
        const nextHeight = `${height}px`;
        if (viewport.style.height !== nextHeight) {
          viewport.style.height = nextHeight;
        }
      });
    }

    function goTo(nextPage, smooth = true) {
      if (nextPage >= pages && pages > 1) {
        looping = true;
        window.clearTimeout(loopResetTimer);
        viewport.scrollTo({ left: pages * viewport.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
        loopResetTimer = window.setTimeout(() => {
          viewport.scrollTo({ left: 0, behavior: 'auto' });
          page = 0;
          looping = false;
          updateControls();
          schedule();
        }, smooth ? 650 : 0);
        return;
      }

      page = Math.max(0, Math.min(pages - 1, nextPage));
      viewport.scrollTo({ left: page * viewport.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      updateControls();
      schedule();
    }

    function buildLoopItems(columnCount) {
      all(track, '[data-bh-review-loop-item]').forEach(item => item.remove());
      const originalCards = all(track, '.bh-review-card:not([data-bh-review-loop-item])');
      const remainder = originalCards.length % columnCount;
      const spacerCount = remainder === 0 ? 0 : columnCount - remainder;

      for (let index = 0; index < spacerCount; index += 1) {
        const spacer = element('span', 'bh-review-card bh-review-card--loop-spacer');
        spacer.dataset.bhReviewLoopItem = '';
        spacer.setAttribute('aria-hidden', 'true');
        spacer.inert = true;
        track.append(spacer);
      }

      originalCards.slice(0, columnCount).forEach(card => {
        const clone = card.cloneNode(true);
        clone.dataset.bhReviewLoopItem = '';
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        track.append(clone);
      });
    }

    function buildPagination() {
      pagination.replaceChildren();
      for (let index = 0; index < pages; index += 1) {
        const dot = element('button', `bh-reviews__dot${index === page ? ' is-active' : ''}`);
        dot.type = 'button';
        dot.setAttribute('aria-label', `Ir a la página ${index + 1} de ${pages}`);
        dot.setAttribute('aria-current', String(index === page));
        dot.addEventListener('click', () => goTo(index), { signal: localSignal });
        pagination.append(dot);
      }
    }

    function refresh() {
      const columnCount = columns();
      if (columnCount === lastColumnCount) {
        setFixedHeight();
        return;
      }
      lastColumnCount = columnCount;
      root.style.setProperty('--bh-review-columns', columnCount);
      pages = Math.max(1, Math.ceil(reviews.length / columnCount));
      page = Math.min(page, pages - 1);
      looping = false;
      window.clearTimeout(loopResetTimer);
      buildLoopItems(columnCount);
      buildPagination();
      setFixedHeight();
      goTo(page, false);
    }

    previousButton.addEventListener('click', () => goTo(page - 1), { signal: localSignal });
    nextButton.addEventListener('click', () => goTo(page + 1), { signal: localSignal });
    viewport.addEventListener('scroll', () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        if (looping) return;
        const nextPage = Math.round(viewport.scrollLeft / Math.max(1, viewport.clientWidth));
        if (nextPage >= pages && pages > 1) {
          window.clearTimeout(loopResetTimer);
          loopResetTimer = window.setTimeout(() => {
            looping = true;
            viewport.scrollTo({ left: 0, behavior: 'auto' });
            page = 0;
            updateControls();
            requestAnimationFrame(() => {
              looping = false;
              schedule();
            });
          }, 140);
          return;
        }
        if (nextPage !== page) {
          page = Math.max(0, Math.min(pages - 1, nextPage));
          updateControls();
          schedule();
        }
      });
    }, { passive: true, signal: localSignal });

    root.addEventListener('mouseenter', () => { paused = true; schedule(); }, { signal: localSignal });
    root.addEventListener('mouseleave', () => { paused = false; schedule(); }, { signal: localSignal });
    root.addEventListener('focusin', () => { paused = true; schedule(); }, { signal: localSignal });
    root.addEventListener('focusout', () => { paused = false; schedule(); }, { signal: localSignal });
    root.addEventListener('load', event => {
      if (event.target instanceof HTMLImageElement) setFixedHeight();
    }, { signal: localSignal, capture: true });

    const intersectionObserver = new IntersectionObserver(entries => {
      visible = Boolean(entries[0]?.isIntersecting);
      schedule();
    }, { threshold: .2 });
    intersectionObserver.observe(root);

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => {
          if (resizeFrame) return;
          resizeFrame = requestAnimationFrame(() => {
            resizeFrame = 0;
            refresh();
          });
        })
      : null;
    resizeObserver?.observe(root);

    refresh();
    return {
      root,
      refresh,
      wake: schedule,
      destroy() {
        localController.abort();
        intersectionObserver.disconnect();
        resizeObserver?.disconnect();
        window.clearTimeout(timer);
        window.clearTimeout(loopResetTimer);
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        if (heightFrame) cancelAnimationFrame(heightFrame);
        if (resizeFrame) cancelAnimationFrame(resizeFrame);
        root.remove();
        source.classList.remove('bh-judge-source');
        delete source.dataset.bhReviewsMounted;
      }
    };
  }

  function mount(source) {
    if (source.dataset.bhReviewsMounted === 'true') return;
    const reviews = collectReviews(source);
    if (!reviews.length) return;

    const existing = source.parentElement?.querySelector(':scope > [data-bh-reviews]');
    existing?.remove();
    source.dataset.bhReviewsMounted = 'true';
    source.classList.add('bh-judge-source');
    const view = build(source, reviews);
    source.before(view.root);
    instances.set(source, createController(source, view));
  }

  function refresh() {
    instances.forEach((instance, source) => {
      if (!source.isConnected) {
        instance.destroy();
        instances.delete(source);
      } else {
        instance.refresh();
      }
    });
    all(document, '.jdgm-carousel-wrapper').forEach(mount);
  }

  document.addEventListener('shopify:section:load', refresh, { signal });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) instances.forEach(instance => instance.wake());
  }, { signal });

  let observerFrame = 0;
  function scheduleDiscovery() {
    if (observerFrame) return;
    observerFrame = requestAnimationFrame(() => {
      observerFrame = 0;
      refresh();
    });
  }

  observer = new MutationObserver(records => {
    const hasNewCarousel = records.some(record => [...record.addedNodes].some(node =>
      node instanceof Element && (node.matches('.jdgm-carousel-wrapper') || node.querySelector?.('.jdgm-carousel-wrapper'))
    ) || record.target instanceof Element && Boolean(record.target.closest('.jdgm-carousel-wrapper')));
    if (hasNewCarousel) scheduleDiscovery();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.BHJudgeReviews = {
    version: VERSION,
    refresh,
    destroy() {
      abortController.abort();
      observer?.disconnect();
      if (observerFrame) cancelAnimationFrame(observerFrame);
      instances.forEach(instance => instance.destroy());
      instances.clear();
      document.documentElement.classList.remove('bh-judge-reviews-ready');
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true, signal });
  else refresh();
})();
